import { useCallback, useState } from 'react';
import { api, errorMessage } from '../lib/api';
import type { ToolKey } from '../lib/session';
import type { SearchType } from './search';

/**
 * "Zugriff beantragen" (Shell 06/13): creates an approval task for the organization's approval recipient
 * through `POST /approvals/access` (the approvals module, M3). Until the route exists the API answers
 * 404 and the banner shows its message — nothing is faked on the client.
 */
export type AccessRequest =
  { kind: 'tool'; tool: ToolKey } | { kind: 'records'; entity: SearchType; ids: string[]; query: string };

export function requestAccess(body: AccessRequest): Promise<void> {
  return api<void>('/approvals/access', { method: 'POST', body });
}

export interface AccessRequestState {
  pending: boolean;
  /** True once the request was accepted by the server. */
  done: boolean;
  error: string | null;
  submit: (body: AccessRequest) => Promise<void>;
  reset: () => void;
}

export function useAccessRequest(): AccessRequestState {
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reset = useCallback(() => {
    setDone(false);
    setError(null);
  }, []);
  const submit = useCallback(async (body: AccessRequest) => {
    setPending(true);
    setError(null);
    try {
      await requestAccess(body);
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }, []);
  return { pending, done, error, submit, reset };
}
